import { useCallback, useMemo } from "react";

import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Trans } from "react-i18next";
import { Ring } from "@uiball/loaders";
import { Controller } from "react-hook-form";

import Button from "../../button/index.jsx";
import TextField from "../../text-field/index2.jsx";
import CheckboxField from "../../checkbox-field/index.jsx";

import { logDebug, logError } from "../../../utils/logger.js";
import { isFalse, isTrue } from "../../../utils/validators.js";

function NewsletterRegForm(props) {
  const { form, loading, onValid, onInvalid, onConsentClick } = props;
  const { control, formState, handleSubmit } = form;
  const { isLoading, isDirty, disabled } = formState;

  const { t } = useTranslation();

  const watchedPrivacyConsent = useWatch({
    name: "consent_privacy",
    control: control,
  });
  const watchedMarketingConsent = useWatch({
    name: "consent_marketing",
    control: control,
  });

  const loadingForm = useMemo(() => {
    return [isTrue(loading), isTrue(isLoading)].some((i) => i);
  }, [loading, isLoading]);

  const disabledSubmit = useMemo(() => {
    return [
      isFalse(isDirty),
      isTrue(disabled),
      isTrue(loadingForm),
      isFalse(watchedPrivacyConsent),
      isFalse(watchedMarketingConsent),
    ].some((i) => i);
  }, [
    isDirty,
    disabled,
    loadingForm,
    watchedPrivacyConsent,
    watchedMarketingConsent,
  ]);

  const onCopyHandler = useCallback((evt) => {
    logDebug("NewsletterForm", "onCopyHandler", { evt: evt });
    evt.preventDefault();
    evt.stopPropagation();
    return false;
  }, []);

  const onPasteHandler = useCallback((evt) => {
    logDebug("NewsletterForm", "onPasteHandler", { evt: evt });
    evt.preventDefault();
    evt.stopPropagation();
    return false;
  }, []);

  const onValidHandler = useCallback(
    (data, evt) => {
      const handler = async (data, evt) => {
        if (!disabled) {
          onValid && onValid(data, evt);
        }
      };
      logDebug("NewsletterRegForm", "onValidHandler", { data: data, evt: evt });
      handler(data, evt);
    },
    [disabled, onValid]
  );

  const onInvalidHandler = useCallback(
    (errors, evt) => {
      const handler = async (errors, evt) => {
        if (!disabled) {
          onInvalid && onInvalid(errors, evt);
        }
      };
      logError("NewsletterRegForm", "onInvalidHandler", {
        errors: errors,
        evt: evt,
      });
      handler(errors, evt);
    },
    [disabled, onInvalid]
  );

  const onClickConsentHandler = useCallback(
    (evt, type) => {
      logDebug("NewsletterRegForm", "onClickConsentHandler", {
        evt: evt,
        type: type,
      });
      onConsentClick && onConsentClick(evt, type);
    },
    [onConsentClick]
  );

  const PrivacyConsentLabel = (props) => {
    const { onClick } = props;

    const onClickHandler = useCallback(
      (evt, type) => onClick && onClick(evt, type),
      [onClick]
    );

    return (
      <Trans
        i18nKey={"reg_newsletter_dialog.form.consent_privacy.template"}
        components={{
          text: (
            <span
              className="text"
              title={t("reg_newsletter_dialog.form.consent_privacy.text")}
            ></span>
          ),
          privacy_link: (
            <span
              role="button"
              className="link privacy"
              onClick={(evt) => onClickHandler(evt, "PRIVACY")}
            ></span>
          ),
        }}
      />
    );
  };

  const MarketingConsentLabel = (props) => {
    return (
      <Trans
        i18nKey={"reg_newsletter_dialog.form.consent_marketing.template"}
        components={{
          text: (
            <span
              className="text"
              title={t("reg_newsletter_dialog.form.consent_marketing.text")}
            ></span>
          ),
        }}
      />
    );
  };

  return (
    <div className="newsletter__form__container">
      {loadingForm && (
        <div className="newsletter__form__loading">
          <Ring size={100} lineWeight={5} speed={2} color="#777e8b" />
        </div>
      )}
      <form
        id="newsletter_register_form"
        className="newsletter__form"
        onSubmit={handleSubmit(onValidHandler, onInvalidHandler)}
      >
        <div className="field email1">
          <Controller
            name="email1"
            control={control}
            disabled={disabled}
            render={({ field, fieldState, formState }) => (
              <TextField
                {...field}
                label={t("reg_newsletter_dialog.form.email1")}
                placeholder={t("reg_newsletter_dialog.form.email1")}
                autoComplete={"email"}
                error={fieldState?.error?.message}
                required
                onCopy={(evt) => onCopyHandler(evt)}
                onPaste={(evt) => onPasteHandler(evt)}
              />
            )}
          />
        </div>
        <div className="field email2">
          <Controller
            name="email2"
            control={control}
            disabled={disabled}
            render={({ field, fieldState, formState }) => (
              <TextField
                {...field}
                label={t("reg_newsletter_dialog.form.email2")}
                placeholder={t("reg_newsletter_dialog.form.email2")}
                autoComplete={"email"}
                error={fieldState?.error?.message}
                required
                onCopy={(evt) => onCopyHandler(evt)}
                onPaste={(evt) => onPasteHandler(evt)}
              />
            )}
          />
        </div>

        <div className="field consent_privacy">
          <Controller
            name="consent_privacy"
            control={control}
            disabled={disabled}
            render={({ field, fieldState, formState }) => (
              <CheckboxField {...field} error={fieldState?.error?.message}>
                <PrivacyConsentLabel
                  onClick={(evt, type) => onClickConsentHandler(evt, type)}
                />
              </CheckboxField>
            )}
          />
        </div>

        <div className="field consent_marketing">
          <Controller
            name="consent_marketing"
            control={control}
            disabled={disabled}
            render={({ field, fieldState, formState }) => (
              <CheckboxField {...field} error={fieldState?.error?.message}>
                <MarketingConsentLabel />
              </CheckboxField>
            )}
          />
        </div>

        <div className="newsletter__form__footer">
          <Button
            type="submit"
            text
            primary
            loading={loadingForm}
            disabled={disabledSubmit}
          >
            <span className="label">{t("button.send")}</span>
          </Button>
        </div>
      </form>
    </div>
  );
}

export default NewsletterRegForm;
